import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { ClientNotification, ShowNotification } from 'app/shared/notifications';
import { ProjectionNotificationsService } from 'app/shared/notifications/projection-notification.service';
import { merge } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { LoadProfessionalDomiciles } from '../member-professional-domicile/member-professional-domicile.actions';
import { MunicipalityUtilsService } from '../municipalities/municipalities-utils.service';
import { MemberResidenceCommandService } from './member-residence-command.service';
import { MemberResidenceQueryService } from './member-residence-query.service';
import { AddMemberResidence, AddMemberResidenceFailure, AddMemberResidenceSuccess, CancelMemberResidenceChange, CancelMemberResidenceChangeFailure, CancelMemberResidenceChangeSuccess, CorrectMemberResidence, CorrectMemberResidenceFailure, CorrectMemberResidenceSuccess, LoadMemberResidences, LoadMemberResidencesFailure, LoadMemberResidencesSuccess } from './member-residence.action';
import { MemberResidence } from './models/member-residence.model';

export class MemberResidenceStateModel {
  residences: MemberResidence[] = [];
  residencesLoading = false;
  _memberId: string;
}

@State<MemberResidenceStateModel>({
  name: 'memberLocalisation',
  defaults: new MemberResidenceStateModel(),
})
@Injectable()
export class MemberResidenceState implements NgxsOnInit {
  constructor(
    private queryService: MemberResidenceQueryService,
    private commandService: MemberResidenceCommandService,
    private municipalitUtilsService: MunicipalityUtilsService,
    private translate: TranslateService,
    private projectionNotificationsService: ProjectionNotificationsService,
  ) { }

  @Selector()
  static allResidences(state: MemberResidenceStateModel): MemberResidence[] {
    return state.residences || [];
  }

  @Selector()
  static currentResidence(state: MemberResidenceStateModel): MemberResidence {
    return state.residences.find(residence => !!residence.isCurrent);
  }

  @Selector()
  static nextResidence(state: MemberResidenceStateModel): MemberResidence {
    return !state.residences ? null : state.residences
      .filter(r => r.effectiveOn.toLocalDate() > new Date())
      .slice().sort((a, b) => a?.effectiveOn?.toLocalDate().getTime() - b?.effectiveOn?.toLocalDate().getTime())?.[0];
  }

  @Selector()
  static residencesLoading(state: MemberResidenceStateModel): boolean {
    return state.residencesLoading;
  }

  ngxsOnInit({ dispatch, getState }: StateContext<MemberResidenceStateModel>) {
    merge(
      this.projectionNotificationsService.when('ResidenceRegisteredEvent'),
      this.projectionNotificationsService.when('ResidenceInformationCorrectedEvent'),
      this.projectionNotificationsService.when('ResidenceCancelledEvent'),
      this.projectionNotificationsService.when('ResidenceDateCorrectedEvent'),
      this.projectionNotificationsService.when('ResidenceDefinedAsProfessionalDomicileEvent'),
    ).subscribe(_ => {
      dispatch(new LoadMemberResidences(getState()._memberId)),
        dispatch(new LoadProfessionalDomiciles(getState()._memberId))
    })
  }

  @Action(LoadMemberResidences)
  onLoadMemberResidences(
    { patchState, dispatch }: StateContext<MemberResidenceStateModel>,
    { memberId }: LoadMemberResidences,
  ) {
    patchState({ residencesLoading: true, _memberId: memberId });
    return this.queryService.getMemberResidences(memberId).pipe(
      tap(result => dispatch(new LoadMemberResidencesSuccess(result))),
      catchError(err => dispatch(new LoadMemberResidencesFailure(err))),
    );
  }

  @Action(LoadMemberResidencesSuccess)
  onLoadMemberResidencesSuccess(
    { patchState }: StateContext<MemberResidenceStateModel>,
    { data }: LoadMemberResidencesSuccess,
  ) {
    patchState({ residences: data, residencesLoading: false });
  }

  @Action(LoadMemberResidencesFailure)
  onLoadMemberResidenceFailure({ patchState }: StateContext<MemberResidenceStateModel>, { error }: LoadMemberResidencesFailure) {
    patchState({ residences: [], residencesLoading: false });
  }

  @Action(AddMemberResidence)
  onAddMemberResidence({ patchState, dispatch }: StateContext<MemberResidenceStateModel>, { memberId, residence }: AddMemberResidence) {
    dispatch(
      new ShowNotification(ClientNotification.info(this.translate.instant('memberResidenceStore.addMemberResidence'))),
    );
    patchState({ residencesLoading: true });
    residence.addressDetail.normalizeId = residence.addressDetail.id;

    // Check for municipality. If exists, Add residence, else add municipality, then residence
    return this.municipalitUtilsService.provideMunipalityCodeForAddress(residence.addressDetail).pipe(
      mergeMap(code => {
        residence.municipalityCode = code;
        return this.commandService.createFutureResidence(memberId, residence);
      }),
      tap(residenceId => dispatch(new AddMemberResidenceSuccess(memberId, residenceId, residence))),
      catchError(err => dispatch(new AddMemberResidenceFailure(err))),
    );
  }

  @Action(AddMemberResidenceSuccess)
  onAddMemberResidenceSuccess(
    { patchState, getState, dispatch }: StateContext<MemberResidenceStateModel>,
    { memberId, residenceId, residence }: AddMemberResidenceSuccess,
  ) {
    // optimistic behaviour
    const newResidence = new MemberResidence(residence, residenceId);
    const newResidenceArray = [...getState().residences, newResidence];
    patchState({ residencesLoading: false, residences: this.sortResidencesAndSetCurrent(newResidenceArray) });
  }

  @Action(AddMemberResidenceFailure)
  onAddMemberResidenceFailure(
    { patchState, dispatch }: StateContext<MemberResidenceStateModel>) {
    patchState({ residencesLoading: false });
    dispatch(
      new ShowNotification(ClientNotification.error(this.translate.instant('memberResidenceStore.addMemberResidenceFailed'))),
    );
  }

  @Action(CorrectMemberResidence)
  onCorrectMemberResidence(
    { patchState, dispatch }: StateContext<MemberResidenceStateModel>,
    { memberId, residence }: CorrectMemberResidence,
  ) {
    dispatch(
      new ShowNotification(ClientNotification.info(this.translate.instant('memberResidenceStore.correctMemberResidence'))),
    );
    patchState({ residencesLoading: true });
    // Check for municipality. If exists, Change residence, else add municipality, then residence
    return this.municipalitUtilsService.provideMunipalityCodeForAddress(residence.addressDetail).pipe(
      mergeMap(code => {
        residence.municipalityCode = code;
        return this.commandService.correctAddress(memberId, residence);
      }),
      tap(_ => dispatch(new CorrectMemberResidenceSuccess(memberId, residence))),
      catchError(err => dispatch(new AddMemberResidenceFailure(err))),
    );
  }

  @Action(CorrectMemberResidenceSuccess)
  onCorrectMemberResidenceSuccess(
    { patchState, getState, dispatch }: StateContext<MemberResidenceStateModel>,
    { residence, memberId }: CorrectMemberResidenceSuccess,
  ) {
    // optimistic behaviour
    const filtered = getState().residences.filter(res => res.id !== residence.id);
    const correctedAddress = new MemberResidence(residence, residence.id);
    const newResidenceArray = [...filtered, correctedAddress];
    patchState({ residencesLoading: false, residences: this.sortResidencesAndSetCurrent(newResidenceArray) });
  }

  @Action(CorrectMemberResidenceFailure)
  onCorrectMemberResidenceFailure({ patchState }: StateContext<MemberResidenceStateModel>) {
    patchState({ residencesLoading: false });
  }

  @Action(CancelMemberResidenceChange)
  onCancelMemberResidenceChange(
    { patchState, dispatch }: StateContext<MemberResidenceStateModel>,
    { memberId, residenceId, effectiveDate, confirmDeleteDomicileProfessionel,
      domicileProfessionelToDeleteId }: CancelMemberResidenceChange,
  ) {
    dispatch(
      new ShowNotification(ClientNotification.info(this.translate.instant('memberResidenceStore.cancelMemberResidenceChange'))),
    );
    patchState({ residencesLoading: true });
    return this.commandService.cancelFutureResidence(memberId, residenceId, effectiveDate,
      confirmDeleteDomicileProfessionel,
      domicileProfessionelToDeleteId).pipe(
        tap(_ => dispatch(new CancelMemberResidenceChangeSuccess(residenceId))),
        catchError(err => dispatch(new CancelMemberResidenceChangeFailure(err))),
      );
  }
  @Action(CancelMemberResidenceChangeSuccess)
  onCancelMemberResidenceChangeSuccess(
    { patchState, getState }: StateContext<MemberResidenceStateModel>,
    { residenceId }: CancelMemberResidenceChangeSuccess,
  ) {
    const result = getState().residences
      .filter(res => res.id !== residenceId);
    patchState({ residencesLoading: false, residences: this.sortResidencesAndSetCurrent(result) });
  }
  @Action(CancelMemberResidenceChangeFailure)
  onCancelMemberResidenceChangeFailure({ patchState }: StateContext<MemberResidenceStateModel>) {
    patchState({ residencesLoading: false });
  }

  private sortResidencesAndSetCurrent(residences: MemberResidence[]): MemberResidence[] {
    const resultResidence = residences.sort((a, b) => b.effectiveOn.toLocalDate().getTime() - a.effectiveOn.toLocalDate().getTime())
      .map(res => ({ ...res, isCurrent: false }));
    // Set IsCurrent = true  for residence effectiveOn closest from today
    const today = new Date().getTime();
    const closestResidence = resultResidence.find(res => res.effectiveOn.toLocalDate().getTime() <= today);
    if (!!closestResidence) {
      closestResidence.isCurrent = true;
    }
    return resultResidence;
  }
}
